// Horizontally-scrollable strip of decrypted thumbnails. Reuses
// the folder's .thumbnails sidecar via useFolderThumbnails so no
// new decryption path is needed. Active tile is outlined; tap
// jumps the carousel.

import { useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { useFolderThumbnails } from "@/hooks/useThumbnail";

interface Props {
  folderPath: string;
  fileNames: string[];
  activeIndex: number;
  onPick: (index: number) => void;
}

export function ThumbnailStrip({
  folderPath,
  fileNames,
  activeIndex,
  onPick,
}: Props) {
  const thumbnails = useFolderThumbnails(folderPath);
  const urls = useMemo(() => {
    const map: Record<string, string> = {};
    const urlList: string[] = [];
    for (const name of fileNames) {
      const bytes = thumbnails[name];
      if (!bytes) continue;
      const url = URL.createObjectURL(
        new Blob([bytes as BlobPart], { type: "image/webp" }),
      );
      map[name] = url;
      urlList.push(url);
    }
    return { map, urlList };
  }, [thumbnails, fileNames]);

  useEffect(() => {
    return () => {
      for (const url of urls.urlList) URL.revokeObjectURL(url);
    };
  }, [urls]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLButtonElement>(
      `[data-active="true"]`,
    );
    if (active) {
      active.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  return (
    <div
      ref={scrollerRef}
      className="flex gap-1 overflow-x-auto overflow-y-hidden px-2 py-2 md:justify-center"
    >
      {fileNames.map((name, i) => (
        <button
          key={name}
          type="button"
          data-active={i === activeIndex}
          onClick={() => onPick(i)}
          className={cn(
            "aspect-square flex-shrink-0 overflow-hidden rounded bg-white/10",
            "h-9 md:h-12",
            i === activeIndex && "outline outline-2 outline-white",
          )}
          aria-label={`Photo ${i + 1} of ${fileNames.length}`}
        >
          {urls.map[name] ? (
            <img
              src={urls.map[name]}
              alt=""
              draggable={false}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-white/5" />
          )}
        </button>
      ))}
    </div>
  );
}
